import { type HTMLAttributes, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../../../../lib/utils";

const VIEWPORT_GAP = 8;
const DEFAULT_OFFSET = -4; // matches -tw-top-1

interface SubmenuProps extends HTMLAttributes<HTMLDivElement> {
    direction: "left" | "right";
}

function Submenu({ direction, className, style, children, ...rest }: SubmenuProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [top, setTop] = useState(DEFAULT_OFFSET);
    const [maxHeight, setMaxHeight] = useState<number>();

    useLayoutEffect(() => {
        const el = ref.current;
        const anchor = el?.parentElement;
        if (!el || !anchor) return;

        const update = () => {
            const viewportHeight = document.documentElement.clientHeight;
            const anchorTop = anchor.getBoundingClientRect().top;
            const height = el.offsetHeight;
            const available = viewportHeight - VIEWPORT_GAP * 2;

            let offset = DEFAULT_OFFSET;
            const bottom = anchorTop + offset + height;
            if (bottom > viewportHeight - VIEWPORT_GAP) {
                offset -= bottom - (viewportHeight - VIEWPORT_GAP);
            }
            // Don't go above the top edge
            if (anchorTop + offset < VIEWPORT_GAP) {
                offset = VIEWPORT_GAP - anchorTop;
            }

            setTop(offset);
            setMaxHeight(available);
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={cn(
                "tw-absolute tw-bg-white tw-rounded tw-shadow-md tw-border tw-p-1 tw-min-w-[300px] tw-overflow-y-auto tw-animate-in tw-fade-in-0",
                direction === "right" ? "tw-left-full tw-ml-1" : "tw-right-full tw-mr-1",
                className
            )}
            style={{ top, maxHeight, ...style }}
            {...rest}
        >
            {children}
        </div>
    );
}

export default Submenu;
